import { z } from 'zod';
import { assignableMemberRoles, type MemberRole } from '../enums.js';
import { emailSchema } from '../ids.js';
import { loginSecondFactorSchema, registerSchema } from './auth.js';

/**
 * Contrat réseau des membres d'un cabinet, de leur profil praticien et des invitations.
 *
 * Le mot de passe et les noms réutilisent les règles de l'inscription (`registerSchema`) : une
 * seule politique de mot de passe pour tout le produit, y compris quand un compte naît d'une
 * invitation.
 */
/** Politique de mot de passe du produit, appliquée à tout mot de passe nouvellement choisi. */
export const accountPasswordSchema = registerSchema.shape.password;
const nameSchema = registerSchema.shape.firstName;

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullable();

/** Couleur d'agenda : code hexadécimal, ou nom de jeton sémantique (voir `docs/audit/audit-client.md`). */
const agendaColorSchema = z
  .string()
  .trim()
  .regex(/^(#[0-9a-fA-F]{6}|[a-z][a-z0-9-]{0,31})$/, 'Couleur d’agenda invalide');

/* -------------------------------------------------------------------------- */
/* Entrées                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Seuls le rôle et l'activation sont modifiables : l'identité appartient au compte, pas au cabinet.
 * `owner` n'est pas un rôle qu'on donne : un cabinet a exactement un propriétaire, qui se
 * **transfère** (`ownershipTransferSchema`).
 */
export const memberUpdateSchema = z
  .object({ role: z.enum(assignableMemberRoles), isActive: z.boolean() })
  .partial()
  .strict()
  .refine((v) => v.role !== undefined || v.isActive !== undefined, 'Aucun champ à modifier');
export type MemberUpdateInput = z.infer<typeof memberUpdateSchema>;

/**
 * Transfert de la propriété du cabinet. Un seul geste, une seule transaction : le membre visé
 * devient propriétaire et l'appelant descend au rôle qu'il indique. Faire les deux en deux appels
 * laisserait le cabinet avec deux propriétaires, ou avec aucun si le second n'arrivait jamais.
 *
 * Le rôle d'arrivée est **exigé** : on ne devine pas ce que devient un praticien qui cède la barre
 * de son cabinet. Il se choisit parmi les rôles qu'on donne — `owner` n'en est pas, ce serait ne
 * rien transférer — et le serveur refuse ceux que l'offre ne permet pas (`planAllowsRole`).
 */
export const ownershipTransferSchema = z
  .object({ demoteCurrentOwnerTo: z.enum(assignableMemberRoles) })
  .strict();
export type OwnershipTransferInput = z.infer<typeof ownershipTransferSchema>;

/** `PUT` : le profil est remplacé en entier, un champ absent serait ambigu. */
export const practitionerProfileSchema = z
  .object({
    title: optionalText(120),
    authorizationNumber: optionalText(60),
    agendaColor: agendaColorSchema.nullable(),
  })
  .strict();
export type PractitionerProfileInput = z.infer<typeof practitionerProfileSchema>;

/** On n'invite pas un propriétaire : il n'y en a qu'un, et il est déjà là. */
export const invitationCreateSchema = z
  .object({ email: emailSchema, role: z.enum(assignableMemberRoles) })
  .strict();
export type InvitationCreateInput = z.infer<typeof invitationCreateSchema>;

/**
 * Acceptation d'une invitation. Compte existant : `password` (et `totpCode` si le second facteur
 * est actif) — accepter ouvre une session, donc cela doit coûter exactement ce que coûte une
 * connexion. Compte à créer : `firstName`, `lastName` et `password`, ce dernier repassé par
 * `accountPasswordSchema` côté serveur une fois qu'on sait qu'il s'agit d'un mot de passe neuf.
 * Une borne unique ici : un minimum de douze caractères sur le champ refuserait par avance le mot
 * de passe existant d'un compte créé sous une politique plus ancienne.
 */
export const invitationAcceptSchema = z
  .object({
    password: z.string().min(1).max(128).optional(),
    firstName: nameSchema.optional(),
    lastName: nameSchema.optional(),
    /** Même second facteur qu'à la connexion : code temporel ou code de secours. */
    totpCode: loginSecondFactorSchema.optional(),
  })
  .strict();
export type InvitationAcceptInput = z.infer<typeof invitationAcceptSchema>;

export const memberIdParamSchema = z.object({ memberId: z.uuid() }).strict();
export const invitationIdParamSchema = z.object({ invitationId: z.uuid() }).strict();
export const invitationTokenParamSchema = z.object({ token: z.string().min(20).max(200) }).strict();

/**
 * Charge utile stockée sur le jeton d'invitation. Relue au lieu d'être supposée : un `jsonb` n'a
 * aucun type garanti côté base, et cette charge décide du cabinet rejoint et du rôle obtenu.
 */
export const invitationPayloadSchema = z
  .object({
    email: z.email(),
    /**
     * Une charge d'avant le 30 septembre 2026 peut encore dire `owner` ou `assistant` : la
     * migration `0082` les réécrit, et une charge restée illisible est ignorée, jamais devinée.
     */
    role: z.enum(assignableMemberRoles),
    organizationId: z.uuid(),
    invitedBy: z.uuid(),
  })
  .strict();
export type InvitationPayload = z.infer<typeof invitationPayloadSchema>;

/* -------------------------------------------------------------------------- */
/* Sorties                                                                     */
/* -------------------------------------------------------------------------- */

export interface PractitionerProfileDto {
  title: string | null;
  authorizationNumber: string | null;
  agendaColor: string | null;
}

export interface MemberDto {
  id: string;
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  role: MemberRole;
  isActive: boolean;
  invitedAt: string | null;
  joinedAt: string | null;
  /** Absent tant que le membre n'a pas rempli son profil professionnel. */
  profile: PractitionerProfileDto | null;
}

/** Les deux membres tels qu'ils ressortent du transfert, pour rafraîchir la liste sans la relire. */
export interface OwnershipTransferDto {
  newOwner: MemberDto;
  previousOwner: MemberDto;
}

export interface InvitationDto {
  id: string;
  email: string;
  role: MemberRole;
  expiresAt: string;
  createdAt: string;
}

/** Lecture publique d'un jeton d'invitation : juste de quoi afficher l'écran d'acceptation. */
export interface InvitationPreviewDto {
  organizationName: string;
  email: string;
  role: MemberRole;
  expiresAt: string;
  /** Vrai si l'adresse a déjà un compte : l'écran demande alors le mot de passe existant. */
  existingAccount: boolean;
}
